import globalAwsCredentials from 'lib/globalAwsCredentials';
import { KinesisClient, PutRecordCommand, PutRecordCommandInput } from '@aws-sdk/client-kinesis';
import config from 'lib/config';
import Cookies from 'js-cookie';
import SessionStartedEvent from 'lib/Tracking/SessionStartedEvent';
import { v4 as uuid } from 'uuid';
import { OptimizelyDecisionsState } from 'redux-implementations/optimizelyDecisions';
import Store from './Store';
import MemoryStore from './MemoryStore';
import * as LocalStorageStore from './LocalStorageStore';
import AutolistEvent, { EventData } from './Event';
import UserData from './User';

const DEFAULT_KINESIS_CONFIG = { apiVersion: '2013-12-02', endpoint: undefined };

function setClientGuidTimestampCookie(clientGuidTimestamp: string): void {
  Cookies.set('client_guid_timestamp', clientGuidTimestamp);
}

function getLocalStorage(): null | typeof localStorage {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function getBestStore(): Store {
  const localstorage = getLocalStorage();

  if (!localstorage) {
    return new MemoryStore();
  }

  try {
    // test to see if localstorage exceeds quota
    const id = uuid();
    localstorage.setItem(id, uuid());
    localstorage.removeItem(id);
  } catch (error) {
    LocalStorageStore.clearAutolistCoreSequenceNumberItems();
    return new MemoryStore();
  }

  return LocalStorageStore;
}

export class EventLogger {
  kinesis?: KinesisClient;

  store: Store;

  user?: UserData;

  optimizelyDecisions?: OptimizelyDecisionsState;

  optimizelyId?: string;

  constructor() {
    this.store = getBestStore();

    setClientGuidTimestampCookie(this.clientGuidTimestamp);
  }

  get kinesisInstance(): KinesisClient | undefined {
    if (this.kinesis) return this.kinesis;

    try {
      this.kinesis = new KinesisClient({
        credentials: globalAwsCredentials,
        ...DEFAULT_KINESIS_CONFIG,
        endpoint: config.eventsKinesisEndpoint,
        region: 'us-east-1',
      });
    } catch {
      this.kinesis = undefined;
    }

    return this.kinesis;
  }

  get clientGuidTimestamp(): string {
    return `${this.clientGuid}.${this.clientFirstTouchedTimestamp}`;
  }

  async touchSession(): Promise<void> {
    if (this.store.touchSession()) {
      await this.logSessionStarted();
    }
  }

  async logSessionStarted(): Promise<void> {
    await this.log(new SessionStartedEvent());
  }

  async log(eventData: EventData): Promise<void> {
    const event = new AutolistEvent({
      data: {
        ata_client_id: this.clientGuidTimestamp,
        user_id: this.user?.id,
        app_config: this.optimizelyDecisions,
        optimizely_id: this.optimizelyId,
        ...eventData,
      },
      store: this.store,
    });

    if (config.debugEvents) {
      console.log(`Autolist event logged`, event.toJSON());
    }

    this.store.incrementSequenceNumber();

    await this.postEventToKinesis(event);
  }

  async postEventToKinesis(event: AutolistEvent): Promise<void> {
    if (config.disableEvents || !this.kinesisInstance) return;

    await this.kinesisInstance.send(new PutRecordCommand(this.createRecord(event)));
  }

  createRecord(event: AutolistEvent): PutRecordCommandInput {
    return {
      StreamName: config.eventsKinesisStreamName,
      Data: Buffer.from(`${JSON.stringify(event.toJSON())}\n`),
      PartitionKey: this.store.getSessionGuid(),
    };
  }

  get clientGuid(): string {
    return this.store.getClientGuid();
  }

  get clientFirstTouchedTimestamp(): number {
    return this.store.getClientFirstTouchedTimestamp();
  }
}

const el = new EventLogger();
export default el;
